import { Component, useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Download, ExternalLink, X } from "lucide-react";
import { buildFacilityQrPayload, buildFacilityQrUrl, isShortNodeQrId } from "lib/utils/facilityQr";
import FacilityQrMetadata from "./FacilityQrMetadata";
import { projectPathLabel } from "lib/utils/projectNodePaths";
import { getProjectNodeQr, issueProjectNodeQr } from "lib/api/getFacilityQr";
import "css/facilityQr.css";

class FacilityQrCode extends Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (this.state.failed) return <p role="alert">{this.props.errorMessage}</p>;
    return <QRCodeCanvas value={this.props.value} size={320} level="M" marginSize={4} title={this.props.title} role="img" aria-label={this.props.title} />;
  }
}

export default function FacilityQrModal({ metadata: initialMetadata, nodeChoices, t, onClose }) {
  const [selectedId, setSelectedId] = useState(initialMetadata?.node_id || "");
  const selected = nodeChoices?.find(node => node.id === selectedId);
  const metadata = nodeChoices ? { ...selected, node_id: selected?.id } : initialMetadata;
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const qrRef = useRef(null);
  const [issued, setIssued] = useState(null);
  const [qrError, setQrError] = useState("");
  const [retry, setRetry] = useState(0);
  const nodeId = metadata?.node_id;
  const qrMetadata = issued?.node_id === nodeId ? { ...metadata, qr_id: issued.qr_id } : null;
  const payload = qrMetadata ? buildFacilityQrPayload(qrMetadata, "https://checklab.co.kr") : "";
  useEffect(() => {
    setIssued(null); setQrError("");
    if (!nodeId) return;
    const controller = new AbortController();
    getProjectNodeQr(nodeId, controller.signal).then(async existing => {
      if (controller.signal.aborted || !existing.success || isShortNodeQrId(existing.data?.qr_id)) return existing;
      return issueProjectNodeQr(nodeId, controller.signal);
    }).then(result => {
      if (controller.signal.aborted) return;
      if (result.success && isShortNodeQrId(result.data?.qr_id)) setIssued(result.data);
      else setQrError(result.status === 403 ? "새 QR 최초 발급에는 파일 수정 권한이 필요합니다. 관리자에게 발급을 요청하세요." : result.message || "QR을 발급하지 못했습니다.");
    }).catch(() => { if (!controller.signal.aborted) setQrError("QR을 발급하지 못했습니다."); });
    return () => controller.abort();
  }, [nodeId, retry]);

  useEffect(() => {
    const previousFocus = document.activeElement;
    closeRef.current?.focus();
    return () => previousFocus?.focus?.();
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
    }
    if (event.key !== "Tab") return;
    const buttons = dialogRef.current.querySelectorAll("button:not(:disabled), select:not(:disabled), a[href]");
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `facility-qr-${metadata.node_id || metadata.upload_id}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="fusionModalBackdrop" role="presentation" onClick={onClose}>
      <div className="fusionDownloadModal facilityQrModal" role="dialog" aria-modal="true"
        aria-labelledby="facility-qr-title" ref={dialogRef} onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}>
        <div className="fusionDownloadModalHeader">
          <h5 id="facility-qr-title">{t("facilityQr.create")}</h5>
          <button type="button" ref={closeRef} className="fusionModalCloseButton" onClick={onClose}
            aria-label={t("facilityQr.close")}><X size={18} aria-hidden="true" /></button>
        </div>
        <div className="facilityQrModalBody">
          {nodeChoices && <label className="facilityQrModal__nodeSelect">QR 코드로 연결할 경로
            <select value={selectedId} onChange={event => setSelectedId(event.target.value)}>
              <option value="">경로를 선택하세요</option>
              {nodeChoices.map(node => <option key={node.id} value={node.id}>{projectPathLabel(node)}</option>)}
            </select>
            {!nodeChoices.length && <span>프로젝트 설정에서 경로를 먼저 등록하세요.</span>}
          </label>}
          <div className="facilityQrCode" ref={qrRef}>
            {payload ? <FacilityQrCode key={payload} value={payload} title={t("facilityQr.title")}
              errorMessage={t("facilityQr.tooLong")} /> : qrError ? <div role="alert"><p>{qrError}</p><button type="button" onClick={() => setRetry(value => value + 1)}>다시 시도</button></div> : <p role="status">{nodeId ? "QR 발급 중…" : "QR 코드로 연결할 경로를 선택하세요."}</p>}
          </div>
          <FacilityQrMetadata metadata={metadata} t={t} />
          <div className="facilityQrModal__actions">
            {payload && <a className="facilityQrButton facilityQrButton--secondary" href={buildFacilityQrUrl(qrMetadata, window.location.origin)}><ExternalLink size={16} aria-hidden="true" />QR 연결 페이지 보기</a>}
            <button type="button" className="facilityQrButton" onClick={downloadQr} disabled={!payload}>
              <Download size={16} aria-hidden="true" />{t("facilityQr.download")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
